/**
 * Submit-leave form with:
 *   - Period selector (full / morning / afternoon / custom HH:mm range);
 *     half-day + custom locked to single-day leaves.
 *   - Personal-leave notice: blue banner reminding user that personal
 *     leave needs a clear, unavoidable reason; reason field becomes
 *     required (10+ chars enforced both client + server-side).
 *   - Emergency-notice gate: yellow banner + checkbox shown when
 *     leave_type is sick and start < 1h ahead, OR personal and start
 *     < 3 days ahead. Submit disabled until the user explicitly
 *     acknowledges.
 */
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Info } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useSubmitLeave, useQuota, useHalfDayWindows } from '../hooks';
import { uploadFile } from '../lib/api-client';
import { useToast } from '../components/ui/toast';
import { Modal } from '../components/ui/Modal';
import {
  LEAVE_PERIODS,
  isEmergencyLeave,
  leavePeriodDays,
  HOURS_PER_DAY,
  type LeaveType,
  type LeavePeriod,
} from '@payroll/shared';

interface FormData {
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  reason: string;
  period: LeavePeriod;
  start_time: string;
  end_time: string;
}

const PERIOD_LABEL: Record<LeavePeriod, string> = {
  full: 'เต็มวัน',
  morning: 'ครึ่งวันเช้า',
  afternoon: 'ครึ่งวันบ่าย',
  custom: 'ลารายชั่วโมง (ระบุเวลาเอง)',
};

/** "2026-07-26" → "อา. 26 ก.ค." (ใช้บอกวันหยุดที่ระบบข้ามให้) */
function thaiDayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00+07:00`).toLocaleDateString('th-TH', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export function LeaveSubmit() {
  const navigate = useNavigate();
  const submit = useSubmitLeave();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: { leave_type: 'sick', period: 'full', start_time: '09:00', end_time: '17:00' },
  });
  const [file, setFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState<{ request_group_id: string; leave_ids: string[]; count: number; unpaid_converted_days?: number; skipped_dates?: Array<{ date: string; reason: string }> } | null>(null);
  const [ackedEmergency, setAckedEmergency] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);
  // Separate from submit.isPending: covers the evidence-upload window
  // *before* the mutation starts, which is otherwise a double-tap race
  // (two submits can both pass the server-side dup check before either
  // insert commits — see leave.ts BACKLOG #4-14).
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();

  const leaveType = watch('leave_type');
  const startDate = watch('start_date');
  const endDate = watch('end_date');
  const period = watch('period');
  const reasonValue = watch('reason') ?? '';
  const customStart = watch('start_time');
  const customEnd = watch('end_time');

  const isSingleDay = !!startDate && (!endDate || startDate === endDate);

  // fb#345 — เวลาครึ่งวันตามกะของวันที่เลือก (เดิมเขียนตาย 08–12 / 13–17)
  const { data: halfDay } = useHalfDayWindows(isSingleDay ? startDate : undefined);
  const hoursPerDay = halfDay?.hours_per_day ?? HOURS_PER_DAY;
  const halfWin = period === 'morning' ? halfDay?.morning : period === 'afternoon' ? halfDay?.afternoon : null;
  const periodLabel = (p: LeavePeriod): string => {
    const w = p === 'morning' ? halfDay?.morning : p === 'afternoon' ? halfDay?.afternoon : null;
    if (!w) return PERIOD_LABEL[p];
    const hrs = Math.round((leavePeriodDays(p, w.start_time, w.end_time, hoursPerDay) * hoursPerDay) * 100) / 100;
    return `${PERIOD_LABEL[p]} (${w.start_time}–${w.end_time} · ${hrs} ชม.)`;
  };

  // เศษวันที่ลากินจริง (ชม. ÷ ชม.ทำงาน/วัน · ไม่นับพักเที่ยง) — โชว์ให้พนักงานเห็นผลกระทบต่อโควตา
  const customDays = useMemo(() => {
    if (period !== 'custom' || !customStart || !customEnd || customEnd <= customStart) return null;
    return leavePeriodDays('custom', customStart, customEnd, hoursPerDay);
  }, [period, customStart, customEnd, hoursPerDay]);
  const halfDays = halfWin ? leavePeriodDays(period, halfWin.start_time, halfWin.end_time, hoursPerDay) : null;

  const isEmergency = useMemo(
    () => (startDate ? isEmergencyLeave(leaveType, startDate) : false),
    [leaveType, startDate],
  );

  // ลากิจเกินสิทธิ์ = ไม่ block แต่ส่วนที่เกินเป็นลาไม่รับค่าจ้าง — เตือนก่อนกดส่ง.
  const { data: quotaData } = useQuota();
  const personalOverflowDays = useMemo(() => {
    if (leaveType !== 'personal' || !startDate) return 0;
    const qd = quotaData as { quota_set?: boolean; personal?: { remaining?: number } } | undefined;
    // fb#104 — ยังไม่ตั้งสิทธิ์ = ไม่ใช่ "เหลือ 0" (ฝั่งเซิร์ฟเวอร์ไม่แปลงเป็นไม่รับค่าจ้าง) → ห้ามเตือนเกินสิทธิ์
    if (qd?.quota_set === false) return 0;
    const q = qd?.personal;
    if (!q || typeof q.remaining !== 'number') return 0;
    const perDay =
      period === 'custom'
        ? (customDays ?? 0)
        : period === 'morning' || period === 'afternoon'
          ? (halfDays ?? 0.5)
          : 1;
    const nDates = (() => {
      const s = new Date(startDate);
      const e = new Date(endDate || startDate);
      if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0;
      return Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
    })();
    const newDays = (nDates > 1 ? 1 : perDay) * nDates; // หลายวัน = เต็มวันเสมอ (ตรง effectivePeriod)
    const overflow = newDays - Math.max(0, q.remaining);
    return overflow > 1e-9 ? Math.round(overflow * 1000) / 1000 : 0;
  }, [leaveType, startDate, endDate, period, customDays, halfDays, quotaData]);

  // Reset ack when underlying conditions change
  useEffect(() => setAckedEmergency(false), [leaveType, startDate]);

  const reasonRequired = leaveType === 'personal';
  const reasonShort = reasonRequired && reasonValue.trim().length < 10;
  const submitDisabled =
    submit.isPending ||
    isUploading ||
    (isEmergency && !ackedEmergency) ||
    (reasonRequired && reasonShort);

  const onSubmit = async (data: FormData) => {
    if (isUploading || submit.isPending) return;
    setIsUploading(true);
    try {
      let evidencePath: string | undefined;
      if (file) evidencePath = await uploadFile(file, 'evidence', file.name);

      // Build dates array
      const dates: string[] = [];
      const start = new Date(data.start_date);
      const end = new Date(data.end_date || data.start_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().slice(0, 10));
      }

      const effectivePeriod: LeavePeriod = dates.length > 1 ? 'full' : data.period;

      const res = (await submit.mutateAsync({
        leave_type: data.leave_type,
        dates,
        reason: data.reason || undefined,
        evidence_path: evidencePath,
        is_emergency: isEmergency && ackedEmergency,
        period: effectivePeriod,
        start_time: effectivePeriod === 'custom' ? data.start_time : undefined,
        end_time: effectivePeriod === 'custom' ? data.end_time : undefined,
      })) as {
        request_group_id: string;
        leave_ids: string[];
        count: number;
        unpaid_converted_days?: number;
        skipped_dates?: Array<{ date: string; reason: string }>;
      };
      setSubmitted(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <PageLayout title="ขอลา">
      <form onSubmit={handleSubmit(onSubmit)} className="card p-4 space-y-4">
        <div>
          <label className="label">ประเภท</label>
          <select {...register('leave_type', { required: true })} className="input">
            <option value="sick">ลาป่วย</option>
            <option value="personal">ลากิจ</option>
            <option value="vacation">ลาพักร้อน</option>
            <option value="unpaid">ลาไม่รับเงินเดือน</option>
            <option value="maternity">ลาคลอด</option>
          </select>
        </div>

        {/* Personal-leave guidance */}
        {leaveType === 'personal' && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
            <div className="flex gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold mb-0.5">การลากิจต้องระบุเหตุผลที่ชัดเจน</div>
                <div>
                  ระบุธุระและความจำเป็นที่ <b>หลีกเลี่ยงไม่ได้</b> และต้องไปทำด้วยตนเอง
                  หากไม่ระบุเหตุผลหรือข้อมูลไม่ชัดเจน
                  อาจไม่ได้รับอนุมัติ หรือถูกขอให้ชี้แจงเพิ่ม
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="label">วันเริ่ม</label>
          <input type="date" {...register('start_date', { required: true })} className="input" />
        </div>
        <div>
          <label className="label">วันสิ้นสุด</label>
          <input
            type="date"
            {...register('end_date', {
              validate: (v, all) =>
                !v || !all.start_date || v >= all.start_date || 'วันสิ้นสุดต้องไม่ก่อนวันเริ่ม',
            })}
            className="input"
          />
          {errors.end_date?.message ? (
            <div className="text-xs text-red-600 mt-1">{errors.end_date.message}</div>
          ) : (
            <div className="text-xs text-gray-500 mt-1">เว้นว่างถ้าลาวันเดียว</div>
          )}
        </div>

        {/* Period selector — same options for every leave_type. Half-day /
            custom only valid on single-day leaves; we disable the picker
            and force "full" on multi-day. */}
        <div>
          <label className="label">ช่วงเวลาที่ลา</label>
          <select
            {...register('period')}
            disabled={!isSingleDay}
            className="input disabled:opacity-50 disabled:bg-gray-100"
          >
            {LEAVE_PERIODS.map((p) => (
              <option key={p} value={p}>{periodLabel(p)}</option>
            ))}
          </select>
          {!isSingleDay && (
            <div className="text-xs text-gray-500 mt-1">
              ลาหลายวันต้องเป็นเต็มวันทั้งหมด — ครึ่งวัน/ระบุเวลา ใช้ได้กับลาวันเดียว
            </div>
          )}
        </div>

        {isSingleDay && period === 'custom' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">เริ่มเวลา</label>
              <input type="time" {...register('start_time', { required: period === 'custom' })} className="input" />
            </div>
            <div>
              <label className="label">สิ้นสุดเวลา</label>
              <input
                type="time"
                {...register('end_time', {
                  required: period === 'custom',
                  validate: (v, all) =>
                    period !== 'custom' || !v || !all.start_time || v > all.start_time ||
                    'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม',
                })}
                className="input"
              />
              {errors.end_time?.message && (
                <div className="text-xs text-red-600 mt-1 col-span-2">{errors.end_time.message}</div>
              )}
            </div>
            {customDays != null && (
              <div className="col-span-2 text-xs text-gray-600 bg-gray-50 rounded-md px-3 py-2">
                คิดเป็น <b>{customDays}</b> วัน (หัก {customDays} วันจากสิทธิ์ลา · {hoursPerDay} ชม. = 1 วัน ·
                ไม่นับพักเที่ยง 12:00–13:00)
              </div>
            )}
          </div>
        )}

        {isSingleDay && halfDays != null && (
          <div className="text-xs text-gray-600 bg-gray-50 rounded-md px-3 py-2">
            ลา {halfWin!.start_time}–{halfWin!.end_time} คิดเป็น <b>{halfDays}</b> วัน
            ({hoursPerDay} ชม. = 1 วัน · ตามกะของวันที่เลือก)
          </div>
        )}

        {/* fb#104 — ยังไม่ตั้งสิทธิ์ลา: บอกตรง ๆ กันพนักงานเลือกลาไม่รับเงินเดือนเองทั้งที่ไม่ต้อง */}
        {(quotaData as { quota_set?: boolean } | undefined)?.quota_set === false && leaveType !== 'unpaid' && (
          <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900">
            ฝ่ายบุคคลยังไม่ได้ตั้งสิทธิ์การลาปีนี้ให้คุณ — ยื่นตามประเภทจริงได้เลย
            ไม่ต้องเปลี่ยนเป็นลาไม่รับเงินเดือน
          </div>
        )}

        {/* ลากิจเกินสิทธิ์ → ไม่ block แต่ส่วนเกินเป็นลาไม่รับค่าจ้าง */}
        {personalOverflowDays > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-sm">
            <div className="flex gap-2">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
              <div className="text-amber-900">
                <div className="font-semibold mb-0.5">ลากิจเกินสิทธิ์คงเหลือ</div>
                ส่วนที่เกิน <b>{personalOverflowDays}</b> วัน จะบันทึกเป็น
                <b> การลาไม่รับค่าจ้าง</b> (วันดังกล่าวจะถูกหักจากเงินเดือน) —
                ยังส่งคำขอได้ตามปกติ
              </div>
            </div>
          </div>
        )}

        {/* Emergency notice */}
        {isEmergency && (
          <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-sm">
            <div className="flex gap-2">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
              <div className="flex-1">
                <div className="font-semibold text-amber-900 mb-1">
                  คำขอนี้เข้าข่ายการลาฉุกเฉิน
                </div>
                <div className="text-amber-900">
                  {leaveType === 'sick'
                    ? 'ลาป่วยควรแจ้งล่วงหน้าอย่างน้อย 1 ชั่วโมง — คำขอนี้แจ้งช้ากว่าเกณฑ์'
                    : 'ลากิจควรแจ้งล่วงหน้าอย่างน้อย 3 วัน — คำขอนี้แจ้งช้ากว่าเกณฑ์'}
                </div>
                <label className="flex items-start gap-2 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ackedEmergency}
                    onChange={(e) => setAckedEmergency(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-amber-900 font-medium">
                    ยินยอมว่าเป็นการลางานฉุกเฉิน
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="label">
            เหตุผล {reasonRequired && <span className="text-red-600">*</span>}
          </label>
          <textarea
            {...register('reason')}
            rows={3}
            className="input"
            onBlur={() => setReasonTouched(true)}
            placeholder={reasonRequired ? 'อธิบายธุระและเหตุที่หลีกเลี่ยงไม่ได้' : ''}
          />
          {reasonRequired && reasonTouched && reasonShort && (
            <div className="text-xs text-red-600 mt-1">
              ระบุเหตุผลอย่างน้อย 10 ตัวอักษร
            </div>
          )}
        </div>

        <div>
          <label className="label">หลักฐาน (ถ้ามี)</label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="input"
          />
        </div>

        <button type="submit" disabled={submitDisabled} className="btn-cta w-full">
          {submit.isPending || isUploading ? 'กำลังส่ง…' : 'ส่งคำขอ'}
        </button>
      </form>

      {/* Success sheet — same pattern as OTSubmit. Non-dismissible to make
          sure the user knows where to find the request next. */}
      <Modal open={!!submitted} onClose={() => navigate('/', { replace: true })}>
        <div className="text-center pt-2">
          <div className="text-5xl mb-2">✅</div>
          <h2 className="text-lg font-bold mb-1">ส่งคำขอลาเรียบร้อย</h2>
          {submitted && submitted.leave_ids[0] && (
            <p className="text-xs text-gray-400 font-mono mb-3">
              {submitted.leave_ids[0]}
              {submitted.count > 1 ? ` + อีก ${submitted.count - 1} วัน` : ''}
            </p>
          )}
          {submitted && (submitted.unpaid_converted_days ?? 0) > 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
              เกินสิทธิ์ลากิจ {submitted.unpaid_converted_days} วัน — บันทึกเป็นการลาไม่รับค่าจ้าง
            </p>
          )}
          {/* วันหยุดที่ระบบตัดออกให้ — บอกให้เห็นชัด ไม่ตัดเงียบ (ลาวันหยุดไม่นับ
              ไม่ตัดโควตา จึงไม่ต้องยื่น) */}
          {submitted && (submitted.skipped_dates?.length ?? 0) > 0 && (
            <div className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 mb-2 text-left">
              <p className="font-medium mb-1">ข้ามวันหยุดให้ {submitted.skipped_dates!.length} วัน</p>
              <ul className="space-y-0.5">
                {submitted.skipped_dates!.map((s) => (
                  <li key={s.date} className="text-xs">
                    {thaiDayLabel(s.date)} — {s.reason}
                  </li>
                ))}
              </ul>
              <p className="text-xs mt-1 text-blue-700">วันหยุดไม่ตัดสิทธิ์ลา จึงไม่ต้องยื่นใบลา</p>
            </div>
          )}
          <p className="text-sm text-gray-600 mb-1">รอผู้บังคับบัญชาอนุมัติ</p>
          <p className="text-xs text-gray-500 mt-2">
            คุณจะได้รับแจ้งทาง LINE เมื่อมีการอัปเดต
          </p>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => navigate('/', { replace: true })}
              className="btn-secondary flex-1"
            >
              เสร็จ
            </button>
            <button
              onClick={() => navigate('/me/requests', { replace: true })}
              className="btn-primary flex-1"
            >
              📋 ดูคำขอของฉัน
            </button>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}
